/**
 * 자주 사용되는 IANA timezone 목록
 * value: IANA timezone string, label: 표시 이름 (UTC 오프셋 포함)
 */
export const COMMON_TIMEZONES = [
	{ value: 'Pacific/Honolulu', label: 'Pacific/Honolulu (UTC-10)' },
	{ value: 'America/Anchorage', label: 'America/Anchorage (UTC-9)' },
	{ value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC-8)' },
	{ value: 'America/Denver', label: 'America/Denver (UTC-7)' },
	{ value: 'America/Chicago', label: 'America/Chicago (UTC-6)' },
	{ value: 'America/New_York', label: 'America/New_York (UTC-5)' },
	{ value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (UTC-3)' },
	{ value: 'Europe/London', label: 'Europe/London (UTC+0)' },
	{ value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
	{ value: 'Europe/Berlin', label: 'Europe/Berlin (UTC+1)' },
	{ value: 'Europe/Helsinki', label: 'Europe/Helsinki (UTC+2)' },
	{ value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' },
	{ value: 'Asia/Dubai', label: 'Asia/Dubai (UTC+4)' },
	{ value: 'Asia/Karachi', label: 'Asia/Karachi (UTC+5)' },
	{ value: 'Asia/Kolkata', label: 'Asia/Kolkata (UTC+5:30)' },
	{ value: 'Asia/Dhaka', label: 'Asia/Dhaka (UTC+6)' },
	{ value: 'Asia/Bangkok', label: 'Asia/Bangkok (UTC+7)' },
	{ value: 'Asia/Shanghai', label: 'Asia/Shanghai (UTC+8)' },
	{ value: 'Asia/Singapore', label: 'Asia/Singapore (UTC+8)' },
	{ value: 'Asia/Seoul', label: 'Asia/Seoul (UTC+9)' },
	{ value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
	{ value: 'Australia/Sydney', label: 'Australia/Sydney (UTC+10/+11)' },
	{ value: 'Pacific/Auckland', label: 'Pacific/Auckland (UTC+12/+13)' },
];

/**
 * timezone 값(IANA string)을 표시용 label로 변환
 * COMMON_TIMEZONES에 있으면 label 반환, 없으면 원본값 반환
 * @param {string} timezone - IANA timezone string
 * @returns {string}
 */
export const getTimezoneLabel = (timezone) => {
	if (!timezone) return '';
	const found = COMMON_TIMEZONES.find(tz => tz.value === timezone);
	return found ? found.label : timezone;
};

/**
 * timeSlots <-> startTime/endTime/breaks 변환 유틸리티
 *
 * Backend는 timeSlots를 근무 시간대 배열로 저장:
 *   [{start:'09:00', end:'12:00'}, {start:'13:00', end:'18:00'}]
 *
 * UI(개인 설정)에서는 시작/종료/휴게 형태로 표시:
 *   {workStart:'09:00', workEnd:'18:00', breaks:[{start:'12:00', end:'13:00'}]}
 */

/**
 * timeSlots 배열 → {workStart, workEnd, breaks} 변환
 * @param {Array} timeSlots - [{start, end, label?}, ...]
 * @returns {{ workStart: string, workEnd: string, breaks: Array<{start: string, end: string}> }}
 */
export const timeSlotsToStartEndBreaks = (timeSlots) => {
	if (!timeSlots || timeSlots.length === 0) {
		return { workStart: '09:00', workEnd: '18:00', breaks: [] };
	}

	const sorted = [...timeSlots].sort((a, b) => a.start.localeCompare(b.start));
	const workStart = sorted[0].start;
	const workEnd = sorted[sorted.length - 1].end;

	const breaks = [];
	for (let i = 0; i < sorted.length - 1; i++) {
		const gapStart = sorted[i].end;
		const gapEnd = sorted[i + 1].start;
		if (gapStart !== gapEnd) {
			breaks.push({ start: gapStart, end: gapEnd });
		}
	}

	return { workStart, workEnd, breaks };
};

/**
 * {workStart, workEnd, breaks} → timeSlots 배열 변환
 * @param {string} workStart - 근무 시작 시간 (HH:mm)
 * @param {string} workEnd - 근무 종료 시간 (HH:mm)
 * @param {Array<{start: string, end: string}>} breaks - 휴게 시간 배열
 * @returns {Array<{start: string, end: string, label: string}>}
 */
export const startEndBreaksToTimeSlots = (workStart, workEnd, breaks = []) => {
	if (!workStart || !workEnd) {
		return [];
	}

	if (breaks.length === 0) {
		return [{ start: workStart, end: workEnd, label: '' }];
	}

	const sortedBreaks = [...breaks]
		.filter(b => b.start && b.end)
		.sort((a, b) => a.start.localeCompare(b.start));

	if (sortedBreaks.length === 0) {
		return [{ start: workStart, end: workEnd, label: '' }];
	}

	const slots = [];
	let cursor = workStart;

	for (const brk of sortedBreaks) {
		if (cursor < brk.start) {
			slots.push({ start: cursor, end: brk.start, label: '' });
		}
		cursor = brk.end;
	}

	if (cursor < workEnd) {
		slots.push({ start: cursor, end: workEnd, label: '' });
	}

	return slots;
};

/**
 * 기본 일정 요약 문자열 생성 (시작~종료 형태)
 * @param {object} base - effectiveBase 객체
 * @param {function} t - 번역 함수
 * @param {Array<string>} dayNames - DAY_NAMES 배열
 * @returns {{ days: string, timeRange: string, breakRange: string }}
 */
export const getBaseSummaryParts = (base, t, dayNames) => {
	if (!base) return { days: '', timeRange: '', breakRange: '' };

	const days = (base.workDays || [])
		.sort((a, b) => a - b)
		.map(d => t(dayNames[d]))
		.join(', ');

	const { workStart, workEnd, breaks } = timeSlotsToStartEndBreaks(base.timeSlots);
	const timeRange = `${workStart} ~ ${workEnd}`;
	const breakRange = breaks.length > 0
		? breaks.map(b => `${b.start} ~ ${b.end}`).join(', ')
		: '';

	return { days, timeRange, breakRange };
};
